import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { AuthContext } from "./AuthContext";
import { getDriverPresence } from "../utils/driverPresence";
import { subscribeFleetDrivers } from "../services/fleetDriverService";

export const DriverContext = createContext();

export function DriverProvider({ children }) {
  const { fleetId, user } = useContext(AuthContext);
  const [snapshot, setSnapshot] = useState({ fleetId: null, uid: null, drivers: [] });
  const [clock, setClock] = useState(0);

  useEffect(() => {
    setSnapshot({ fleetId, uid: user?.uid, drivers: [] });
    if (!fleetId || !user?.uid) return undefined;
    let active = true;
    let details = {};
    let registrations = {};
    const publish = () => {
      if (!active) return;
      setSnapshot({
        fleetId, uid: user.uid,
        drivers: Object.values(registrations).map(registration => ({
          ...registration, ...details[registration.id], id: registration.id,
          name: details[registration.id]?.name || registration.name || registration.displayName || "Driver",
          tags: registration.tags || [],
          lastPosition: registration.lastPosition || details[registration.id]?.lastPosition || null,
          detailsLoaded: Object.prototype.hasOwnProperty.call(details, registration.id),
        })),
      });
    };
    const stopRegistrations = onSnapshot(collection(db, "fleets", fleetId, "registered_drivers"), result => {
      registrations = Object.fromEntries(result.docs.map(doc => [doc.id, { ...doc.data(), id: doc.id }]));
      publish();
    }, error => {
      registrations = {};
      details = {};
      publish();
      console.error("Error listening to registered drivers:", error);
    });
    const stopDetails = subscribeFleetDrivers(fleetId, drivers => {
      details = Object.fromEntries(drivers.map(driver => [driver.id, driver]));
      publish();
    }, error => {
      // Never retain private details after authorization is revoked.
      details = {};
      publish();
      console.error("Error loading fleet driver details:", error);
    });
    return () => { active = false; stopRegistrations(); stopDetails(); };
  }, [fleetId, user?.uid]);

  useEffect(() => {
    const timer = setInterval(() => setClock(value => value + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const driversDetails = useMemo(() => {
    if (snapshot.fleetId !== fleetId || snapshot.uid !== user?.uid) return [];
    return snapshot.drivers.map(driver => ({
      ...driver,
      isOnlineSignal: typeof driver.isOnline === "boolean" ? driver.isOnline : null,
      isOnline: getDriverPresence(driver.isOnline, driver.lastSeen).isOnline,
    }));
  }, [snapshot, fleetId, user?.uid, clock]);
  const driversDetailsById = useMemo(() => Object.fromEntries(driversDetails.map(driver => [driver.id, driver])), [driversDetails]);
  const lastPosition = useMemo(() => Object.fromEntries(driversDetails.map(driver => [driver.id, driver.lastPosition || null])), [driversDetails]);

  return (
    <DriverContext.Provider value={{ driversList: driversDetails, driversDetails, driversDetailsById, lastPosition }}>
      {children}
    </DriverContext.Provider>
  );
}

export default DriverContext;
